import * as React from 'react';
import {useEffect, useState} from 'react';
import Slider from 'react-slick';
import classNames from 'classnames';

import { useDeviceStore, selectIsMobile, selectIsTablet } from 'features/ui/store/useDeviceStore';

import './style.scss';

import {P} from '@atoms/Text/P';
import {Direction, NavArrow} from '@atoms/LinksAndButtons/NavArrow';
import {ReadMore} from '@atoms/LinksAndButtons/ReadMore';
import {Container} from '@atoms/Layout/Container';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface Props {
    className?: string;
    id?: string;

    children: React.ReactNode;
    slidesToShow?: number;
    initialSlide?: number;

    wide?: boolean;
    center?: boolean;
    centerPadding?: string;
    staticWidth?: boolean;
    dark?: boolean;

    header?: string;
    // Rubriknivå för slider-rubriken. Utelämnad → H3.
    level?: HeadlineLevel;
    text?: string;
    textHtml?: string;
    link?: string;
    linkText?: string;
}

export const AccessibleSlider: React.FunctionComponent<Props> = ({
    className, id,
    children, slidesToShow, initialSlide = 0,
    wide, center = false, centerPadding = '0', staticWidth = false, dark = false, 
    header, level, text, textHtml, link, linkText
}) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);

    const [sliderRef, setSliderRef] = useState<Slider | null>(null);
    const [currentSlide, setCurrentSlide] = useState(initialSlide);
    const [numberOfSlides, setNumberOfSlides] = useState<number>(0);
    const [hasSlided, setHasSlided] = useState<boolean>(false);

    const _sliderRef = React.createRef<HTMLDivElement>();
    const visibleSlides: number = slidesToShow ? slidesToShow : isTablet ? 2 : (isMobile || wide) ? 1 : 3;
    const scrollable: boolean = numberOfSlides > visibleSlides;
    let firstClientX: number = 0;
    let clientX: number = 0;

    const settings = {
        arrows: false, // Hide default arrows
        beforeChange: (_: number, next: number) => setCurrentSlide(next), // Track current slide
        centerMode: center,
        centerPadding: centerPadding,
        dots: true,
        infinite: false,
        initialSlide: initialSlide,
        slidesToScroll: 1,
        slidesToShow: visibleSlides,
        speed: 500,
        variableWidth: !staticWidth,
    };

    // Handle keyboard navigation (Left/Right arrow keys)
    const handleKeyDown = (e: KeyboardEvent) => {
        if (!sliderRef) return;

        if (e.key === "ArrowRight" && currentSlide < numberOfSlides - 1) {
            sliderRef.slickNext();
        } else if (e.key === "ArrowLeft" && currentSlide > 0) {
            sliderRef.slickPrev();
        }
    };
    const setTabIndex = (selector: string, tabIndex: string) => {
        // Update tabIndex for all links inside slides
        document.querySelectorAll(selector).forEach((slide, _index) => {
            const links = slide.querySelectorAll("a, button, input, select, textarea");
            links.forEach((link) => {
                link.setAttribute("tabindex", tabIndex);
            });
        });
    };
    const touchStart = (e: TouchEvent) => {
        firstClientX = e.touches[0].clientX;
    };
    const preventTouch = (e: TouchEvent) => {
        const minValue = 5; // threshold

        clientX = e.touches[0].clientX - firstClientX;

        // Vertical scrolling does not work when you start swiping horizontally.
        if(Math.abs(clientX) > minValue){
            e.preventDefault();
            e.returnValue = false;
            return false;
        }

        return;
    };

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [sliderRef, currentSlide]);

    useEffect(() => {
        setNumberOfSlides(React.Children.toArray(children).filter((child) => child !== null).length);
    }, [children]);

    useEffect(() => {
        setTabIndex(".slick-slide", "-1");
        setTabIndex(".slick-slide.slick-active", "0");
    }, [currentSlide]);

    useEffect(() => {
        // remove vertical scroll on touch
        if(_sliderRef.current) {
            _sliderRef.current.addEventListener('touchstart', touchStart);
            _sliderRef.current.addEventListener('touchmove', preventTouch, {passive: false});
        }

        setTabIndex(".slick-dots", "-1");
        setTabIndex(".slick-slide", "-1");
        setTabIndex(".slick-slide.slick-active", "0");
        if (initialSlide) setCurrentSlide(initialSlide);

        setNumberOfSlides(React.Children.toArray(children).filter((child) => child !== null).length);
        //@ts-expect-error
        const target = _sliderRef.current.querySelector('.slick-track');

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((_mutationRecord) => {
                //@ts-expect-error
                if(target.style.webkitTransform !== "translate3d(0px, 0px, 0px)" && !hasSlided ) {
                    setHasSlided(true);
                }
            });
        });
        //@ts-expect-error
        observer.observe(target, { attributes : true, attributeFilter : ['style'] });
    }, []);

    const sliderClasses = classNames('accessible-slider', className, {
        'accessible-slider__wide-cards': wide,
        'accessible-slider__solo': numberOfSlides === 1,
        'accessible-slider__two': numberOfSlides === 2,// && !wide,
    });

    return (
        <div
            id={id}
            className={sliderClasses}
            ref={_sliderRef}
            aria-roledescription="carousel"
            aria-label="Image slider"
        >
            <Container>
                <div className={dark ? 'accessible-slider__description dark-bg' : 'accessible-slider__description'}>
                    {header &&
                        <Headline level={level ?? HeadlineLevel.H3}>{header}</Headline>
                    }
                    {text &&
                        <P>
                            {text}
                        </P>
                    }
                    {textHtml &&
                        <P dangerouslySetInnerHTML={{__html: sanitizeHtml(textHtml)}} />
                    }
                    {link &&
                        <P>
                            <ReadMore
                                link={link}
                                text={linkText
                                    ? linkText
                                    : undefined}
                            />
                        </P>
                    }
                </div>
            </Container>
            {/* Slider */}
            <Slider ref={setSliderRef} {...settings}>
                {React.Children.map(children, (child, index) => {
                    if(child === null) return;

                    return <div
                        key={index}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`Slide ${index + 1} of ${numberOfSlides}`}
                    >
                        {child}
                    </div>;
                }
                )}
            </Slider>

            {/* Custom Navigation */}
            <Container className="accessible-slider__nav" aria-label="Slide navigation">
                {scrollable &&
                    <NavArrow
                        className='slick-prev'
                        direction={Direction.PREV}
                        onClick={() => sliderRef?.slickPrev()}
                        disabled={currentSlide === 0}
                        aria-disabled={currentSlide === 0}
                        aria-label="Previous slide"
                    />
                }
                {isMobile && numberOfSlides > 1 &&
                    <span className='slick-counter'>/ {numberOfSlides}</span>
                }
                {scrollable &&
                    <NavArrow
                        className='slick-next'
                        direction={Direction.NEXT}
                        onClick={() => sliderRef?.slickNext()}
                        disabled={currentSlide === numberOfSlides - visibleSlides}
                        aria-disabled={currentSlide === numberOfSlides - visibleSlides}
                        aria-label="Next slide"
                    />
                }
            </Container>
        </div>
    );
};
